import { motion } from "framer-motion";
import { AlertTriangle, Network, TrendingUp, Sparkles, Users, MoonStar, FileClock } from "lucide-react";

const hoverPop = {
  whileHover: { y: -10, scale: 1.025 },
  transition: { type: "spring", stiffness: 300, damping: 20 },
};

const cardEnter = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const bottlenecks = [
  { n: "1", title: "Concurrency", desc: "Can only handle one call at a time." },
  { n: "2", title: "The 6 PM Void", desc: "Zero after-hours coverage." },
  { n: "3", title: "Web-Form Latency", desc: "Manual data entry delays." },
];

export default function ProblemBento() {
  return (
    <section id="problem" className="relative bg-pearl py-24 md:py-32">
      <div className="container-x">
        {/* section intro */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mb-14 max-w-3xl text-center"
        >
          <span className="eyebrow">The Core Problem &rarr; Profit</span>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[0.95] tracking-tight text-ink md:text-6xl">
            From leak to <span className="grad-text">locked revenue.</span>
          </h2>
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:auto-rows-[minmax(0,1fr)]">
          {/* Card 1 — The Problem (large) */}
          <motion.div
            variants={cardEnter}
            custom={0}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            {...hoverPop}
            className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-xl md:col-span-2 md:p-10"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-500">
              <AlertTriangle size={24} />
            </span>
            <h3 className="mt-6 font-display text-3xl font-bold tracking-tight text-ink md:text-4xl">
              The Systemic Lead Leak.
            </h3>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-soft">
              You are spending thousands on ads to generate high-intent leads, but your human front desk is physically
              incapable of responding to all of them instantly. The result? Your leads go cold while waiting for a callback.
            </p>
          </motion.div>

          {/* Card 2 — The Bottlenecks (tall) */}
          <motion.div
            variants={cardEnter}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            {...hoverPop}
            className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-xl md:row-span-2"
          >
            <h3 className="font-display text-2xl font-bold tracking-tight text-ink">The Human Bottlenecks.</h3>
            <div className="mt-8 space-y-6">
              {bottlenecks.map((b, i) => {
                const Icon = [Users, MoonStar, FileClock][i];
                return (
                  <div key={b.n} className="flex items-start gap-4">
                    <span className="relative mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-500">
                      <Icon size={20} />
                      <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                        !
                      </span>
                    </span>
                    <div>
                      <p className="font-semibold text-ink">
                        {b.n}. {b.title}
                      </p>
                      <p className="mt-0.5 text-sm text-ink-muted">{b.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Card 3 — The Solution (wide) */}
          <motion.div
            variants={cardEnter}
            custom={2}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            {...hoverPop}
            className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-xl md:col-span-2 md:p-10"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent-dark">
              <Network size={24} />
            </span>
            <h3 className="mt-6 font-display text-3xl font-bold tracking-tight text-ink">The Omnichannel Bridge.</h3>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-soft">
              S1EP Velocity replaces human delay with autonomous speed. It acts as an invisible net that catches every
              missed call and web form, engaging the prospect across Voice and WhatsApp simultaneously in under 30 seconds.
            </p>
          </motion.div>

          {/* Card 4 — The Profit (highlight, full-width banner) */}
          <motion.div
            variants={cardEnter}
            custom={3}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            whileHover={{ y: -10, scale: 1.015 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-accent to-accent-dark p-8 text-white shadow-xl md:col-span-3 md:p-12"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 left-1/3 h-56 w-56 rounded-full bg-accent-light/30 blur-3xl" />
            <div className="relative grid items-center gap-6 md:grid-cols-2 md:gap-12">
              <div>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 text-white">
                  <TrendingUp size={24} />
                </span>
                <h3 className="mt-6 flex items-center gap-3 font-display text-4xl font-bold tracking-tight md:text-5xl">
                  Captured Revenue
                  <Sparkles size={26} className="text-amber-300" />
                </h3>
              </div>
              <p className="text-lg leading-relaxed text-white/90 md:text-xl">
                Stop funding your competitors. By eliminating the response delay, you maximize the ROI on your existing
                marketing spend, turning previously lost leads into locked calendar appointments.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
